import { useRef, useState, useEffect } from "react";
import {
  ActionIcon,
  Blockquote,
  Box,
  Card,
  Group,
  SimpleGrid,
  Button,
  Pill,
  Title,
  Text,
  useMantineTheme,
} from "@mantine/core";

import { useMediaQuery } from "@mantine/hooks";
import { svgIconReset } from "./helpers/GetAQuoteHelpers";
import { useStickyFooter } from "./hooks/useStickyFooter";

import {
  ClockCheckIcon,
  OneWayIcon,
  RoundTripIcon,
  ShuttleIcon,
  TeamIcon,
  CircleCheckIcon,
  LightningIcon,
  KeyIcon,
} from "./icons";

const TRIP_TYPES = [
  {
    id: "round_trip",
    label: "Round Trip",
    description: "Point A to B and back to A",
    icon: RoundTripIcon,
    instantQuote: true,
  },
  {
    id: "one_way",
    label: "One way",
    description: "A simple one-way trip, with optional stops in-between",
    icon: OneWayIcon,
    instantQuote: true,
  },
  {
    id: "custom",
    label: "Custom",
    description: "Build your trip with all the stops that meets your needs",
    icon: KeyIcon,
  },
  {
    id: "other",
    label: "Other",
    description: "When all the details of trip are in works",
    icon: ClockCheckIcon,
  },
  {
    id: "shuttle",
    label: "Continuous local shuttle",
    description: "Move people in between locations in 2 or more trips",
    icon: ShuttleIcon,
  },
  {
    id: "large_event",
    label: "Large event",
    description: "For complex or pending transportation needs",
    icon: TeamIcon,
  },
];

export default function TripTypeSelector({ onNext, tripType, setTripType }) {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { nextButtonAnchorRef, isAtBottom } = useStickyFooter(isMobile);

  return (
    <Box pb={isMobile ? "90px" : 0}>
      <style>{svgIconReset}</style>
      <Title order={2} ta="center" mt="40px" mb="20px">
        First, select your type of trip:Choose your trip type
      </Title>

      <SimpleGrid
        cols={{ base: 2, sm: 2, lg: 3 }}
        spacing="xs"
        verticalSpacing={{ base: "md", sm: "xl" }}
      >
        {TRIP_TYPES.map((type) => {
          const Icon = type.icon;
          return (
            <Card
              key={type.id}
              padding="lg"
              radius="5px"
              withBorder
              onClick={() => setTripType(type.id)}
              styles={(theme) => ({
                root: {
                  cursor: "pointer",
                  borderWidth: tripType === type.id ? 5 : 2,
                  borderStyle: "solid",
                  borderColor:
                    tripType === type.id
                      ? theme.colors[theme.primaryColor][6]
                      : "#dee2e6",
                },
              })}
            >
              <Group justify="space-between" wrap="nowrap">
                {type.instantQuote ? (
                  <Pill
                    styles={(theme) => ({
                      root: {
                        backgroundColor: theme.colors.primary[0],
                        borderRadius: "30px",
                        color: theme.colors.primary[6],
                        fontWeight: 600,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "0px 10px",
                      },
                    })}
                  >
                    <LightningIcon
                      stroke={1.5}
                      width={12}
                      height={12}
                      style={{
                        color: "inherit",
                        verticalAlign: "middle",
                        marginRight: "4px",
                        marginBottom: "2px",
                      }}
                    />
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        fontSize: isMobile ? "9px" : "12px",
                      }}
                    >
                      Instant Quote
                    </span>
                  </Pill>
                ) : (
                  <div />
                )}
                <ActionIcon
                  variant="transparent"
                  size="sm"
                  aria-label="Settings"
                >
                  <Icon
                    style={{
                      color:
                        tripType === type.id
                          ? theme.colors[theme.primaryColor][6]
                          : "#7E7E7E",
                    }}
                    stroke={1.5}
                    width={16}
                    height={16}
                  />
                </ActionIcon>
              </Group>
              <Text mt="10" fw={600}>
                {type.label}
              </Text>
              <Text mt="5" size="sm" c="dimmed">
                {type.description}
              </Text>
            </Card>
          );
        })}
      </SimpleGrid>
      {["shuttle", "other"].includes(tripType) && (
        <Blockquote
          w="100%"
          p={15}
          mt={15}
          styles={(theme) => ({
            root: {
              backgroundColor: theme.colors.primary[0],
              borderLeft: "none",
              borderRadius: "6px",
              padding: theme.spacing.md,
              color: theme.colors.primary[6],
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              gap: "20px",
            },
          })}
        >
          <Box style={{ flexShrink: 0 }}>
            <CircleCheckIcon
              stroke={1.5}
              width={17}
              height={17}
              style={{ color: theme.colors.primary[6] }}
            />
          </Box>{" "}
          Great! Our team will follow up to provide exact pricing after
          reviewing your trip information
        </Blockquote>
      )}

      {/* The Next button anchor */}
      <div
        ref={nextButtonAnchorRef}
        style={{
          height: "1px",
          width: "100%",
          marginTop: isMobile ? "20px" : "0px",
        }}
      />

      {isMobile ? (
        <Box
          style={
            isAtBottom
              ? {
                  width: "100%",
                }
              : {
                  position: "fixed",
                  bottom: 0,
                  width: "100vw",
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 9999,
                  backgroundColor: "white",
                }
          }
        >
          <Button
            fullWidth
            size="xl"
            disabled={!tripType}
            onClick={onNext}
            radius={0}
            h={60}
            style={{
              color: "white",
              backgroundColor: !tripType ? "#E9ECEF" : theme.colors.primary[6],
              fontWeight: 600,
              border: "none",
              fontFamily: '"Avenir Next", sans-serif',
            }}
          >
            Next
          </Button>
        </Box>
      ) : (
        <Button
          fullWidth
          mt={"xl"}
          mb={"xl"}
          size={"lg"}
          disabled={!tripType}
          onClick={onNext}
        >
          Next
        </Button>
      )}
    </Box>
  );
}
